import React from 'react';
import { motion } from 'framer-motion';
import { UserCircle2, Video, DollarSign, Rocket, Play, MessageCircle, Bell, BarChart3 } from 'lucide-react';
import { SectionHeader } from './HowItWorksSection';
import AnimatedNumber from './AnimatedNumber';

const features = [
  { icon: UserCircle2, title: 'Conte sua história', desc: 'Mostre quem você é, sua equipe e o que torna seu negócio único.' },
  { icon: Video, title: 'Apresente sua ideia em vídeo', desc: 'Um vídeo curto fala mais que mil palavras. Chame a atenção dos investidores certos.' },
  { icon: DollarSign, title: 'Receba o investimento', desc: 'Quando alguém acreditar na sua ideia, é só conversar e combinar os detalhes.' },
  { icon: Rocket, title: 'Veja seu sonho crescer', desc: 'Acompanhe seu progresso, conquiste clientes e transforme sua ideia em realidade.' },
];

const ForEntrepreneursSection = () => {
  return (
    <section id="entrepreneurs" className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeader
          gold
          eyebrow="Para quem tem uma ideia"
          title={<>Seu negócio merece <span className="gradient-text-gold">uma chance de crescer</span>.</>}
          subtitle="Você tem a ideia. A gente conecta com quem pode acreditar nela. Simples, direto e sem burocracia."
        />

        <div className="mt-16 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Mobile mockup */}
          <div className="order-2 lg:order-1">
            <PhoneMockup />
          </div>

          {/* Features */}
          <div className="order-1 lg:order-2 space-y-4">
            {features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                whileHover={{ x: 6 }}
                className="glass-card-gold rounded-2xl p-5 sm:p-6 flex gap-4 spotlight-card"
              >
                <div className="shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#ffa726]/25 to-[#ff7043]/10 text-[#ffd54f] flex items-center justify-center">
                  <f.icon size={20} />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-white">{f.title}</h3>
                  <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{f.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const PhoneMockup = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, rotate: -3 }}
      whileInView={{ opacity: 1, y: 0, rotate: -3 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{ duration: 0.9 }}
      className="relative max-w-sm mx-auto"
    >
      {/* Glow background */}
      <div className="absolute -inset-10 bg-gradient-to-br from-[#ffa726]/20 to-[#ff7043]/10 rounded-full blur-3xl" />

      {/* Phone frame */}
      <div className="relative bg-gradient-to-b from-[#1a1f3a] to-[#0a0e1f] rounded-[2.5rem] p-2 border border-white/10 shadow-2xl glow-gold">
        <div className="bg-[#050816] rounded-[2rem] overflow-hidden aspect-[9/19] relative">
          {/* Notch */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-5 bg-black rounded-full z-20" />

          {/* Status bar */}
          <div className="flex items-center justify-between px-6 pt-3 text-[9px] text-white/80 font-medium">
            <span>9:41</span>
            <span></span>
          </div>

          {/* App content */}
          <div className="px-4 pt-8 pb-4 space-y-3.5">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#ffa726] to-[#ff7043] flex items-center justify-center text-xs font-bold text-black">M</div>
                <div>
                  <div className="text-[10px] text-slate-400 leading-none">Bem-vinda,</div>
                  <div className="text-xs font-semibold text-white leading-tight mt-0.5">Mariana 👋</div>
                </div>
              </div>
              <div className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center text-slate-300">
                <Bell size={13} />
              </div>
            </div>

            {/* Captation card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="glass-card-gold rounded-2xl p-3.5"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-[9px] uppercase tracking-widest text-[#ffd54f]">Já recebido</div>
                <div className="text-[9px] text-slate-400">68% da meta</div>
              </div>
              <div className="text-xl heading-display font-bold gradient-text-gold">
                R$ <AnimatedNumber end={340} />k
              </div>
              <div className="text-[9px] text-slate-400">de R$ 500k</div>
              <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: '68%' }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.2, delay: 0.6 }}
                  className="h-full rounded-full"
                  style={{ background: 'linear-gradient(90deg, #ffa726, #ff7043)' }}
                />
              </div>
            </motion.div>

            {/* Pitch video */}
            <div className="relative aspect-video rounded-xl overflow-hidden bg-gradient-to-br from-[#1e3a8a]/40 to-[#ff7043]/30">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(79,172,254,0.6),transparent_60%)]" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-10 h-10 rounded-full bg-white/95 flex items-center justify-center text-[#050816]">
                  <Play size={14} fill="currentColor" />
                </div>
              </div>
              <div className="absolute bottom-2 left-2.5 right-2.5 flex items-center justify-between text-[9px] text-white">
                <span className="font-semibold">Minha ideia · 1:24</span>
                <span className="bg-black/40 px-1.5 py-0.5 rounded">HD</span>
              </div>
            </div>

            {/* Messages */}
            <div className="space-y-1.5">
              <div className="text-[9px] uppercase tracking-widest text-slate-500">Investidores interessados</div>
              {[
                { name: 'Roberto C.', msg: 'Adorei sua ideia! Vamos conversar?', col: '#4facfe', new: true },
                { name: 'Helena M.', msg: 'Quero fazer parte do seu projeto.', col: '#ffa726', new: false },
              ].map((m, i) => (
                <div key={i} className="glass-card rounded-xl px-2.5 py-2 flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-bold text-black" style={{ background: m.col }}>
                    {m.name[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-semibold text-white leading-tight">{m.name}</div>
                    <div className="text-[9px] text-slate-400 truncate">{m.msg}</div>
                  </div>
                  {m.new && <div className="w-1.5 h-1.5 rounded-full bg-green-400" />}
                </div>
              ))}
            </div>

            {/* Bottom nav */}
            <div className="glass-card rounded-full p-1.5 flex items-center justify-around">
              {[BarChart3, MessageCircle, Video, UserCircle2].map((Icon, i) => (
                <div key={i} className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  i === 0 ? 'bg-gradient-to-br from-[#ffa726] to-[#ff7043] text-black' : 'text-slate-400'
                }`}>
                  <Icon size={13} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Floating notification */}
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="absolute -top-4 -right-6 sm:-right-10 glass-card rounded-2xl px-4 py-3 flex items-center gap-3 glow-blue float-slow max-w-[220px]"
      >
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#4facfe] to-[#1e90ff] flex items-center justify-center shrink-0">
          <DollarSign size={16} className="text-white" />
        </div>
        <div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Nova oferta</div>
          <div className="text-xs font-bold text-white">R$ 5.000</div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: -30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.8 }}
        className="absolute bottom-10 -left-4 sm:-left-10 glass-card-gold rounded-2xl px-4 py-3 flex items-center gap-3 glow-gold float-med max-w-[220px]"
      >
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#ffa726] to-[#ff7043] flex items-center justify-center shrink-0">
          <Rocket size={16} className="text-black" />
        </div>
        <div>
          <div className="text-[10px] text-[#ffd54f] uppercase tracking-wider">+12 pessoas</div>
          <div className="text-xs font-bold text-white">viram sua ideia</div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ForEntrepreneursSection;
